import { useCallback, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useRouter } from '@tanstack/react-router';
import { accessQueries } from '../api/access-queries';
import {
  createPermissionChecker,
  DENY_ALL,
  type PermissionChecker,
} from '../lib/permission-set';
import type { PermissionKey } from './catalog';
import { canAccessRoutes } from './route-permission';

/**
 * The current user's permission checker. Denies everything until the grant
 * list has loaded — in the app the root route prefetches it before anything
 * renders, so gated UI never flashes in and back out.
 */
export function usePermissions(): PermissionChecker {
  const { data } = useQuery(accessQueries.mine());
  return useMemo(
    () => (data ? createPermissionChecker(data.permissions) : DENY_ALL),
    [data],
  );
}

/** Whether the current user may do `key`. */
export function useCan(key: PermissionKey): boolean {
  return usePermissions()(key);
}

/**
 * Whether the current user may open an in-app `path` — every route in the
 * branch it matches must be permitted (their `staticData.permission`). Used
 * to filter navigation without repeating the routes' keys.
 */
export function useCanVisit(): (path: string) => boolean {
  const router = useRouter();
  const can = usePermissions();
  return useCallback(
    (path: string) =>
      canAccessRoutes(
        router
          .getMatchedRoutes(path)
          .matchedRoutes.map((route) => route.options.staticData),
        can,
      ),
    [router, can],
  );
}
